import { Injectable } from '@angular/core';
import {
  catchError,
  concatMap,
  delay,
  finalize,
  map,
  retryWhen,
  shareReplay,
  tap,
} from 'rxjs/operators';
import { defer, Observable, of, throwError } from 'rxjs';

import { OnSiteLocationKeys } from '@config/keys/on-site-location.keys';

import { OnSiteLocationStoreDispatcher } from '@common/dispatchers/on-site-location.dispatcher';
import { OnSiteLocationStoreSelector } from '@common/selectors/on-site-location.selector';
import { PaymentAtTableStoreDispatcher } from '@common/dispatchers/payment-at-table.dispatcher';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';

import { DoodOnSiteLocationModel } from '@core/models/on-site-location.model';
import { IPaygreenPaymentIntent, IStripePaymentIntent } from '@core/models/payment.model';
import { DoodOpenCheck, DoodOpenCheckTransaction } from '@core/models/check.model';

import { OnSiteLocationsApiService } from '@core/services/api/on-site-locations/on-site-locations-api.service';
import { PAT_MOCK_CHECK_ID } from '@config/values/pat.values';
import { DoodPaymentAtTableAmountType } from '@store/payment-at-table/payment-at-table.enum';
import { DoodPaymentMethodTypeModel } from '@store/order/order.model';
@Injectable({
  providedIn: 'root',
})
export class OnSiteLocationsService {
  private openCheckInFlightByLocationId = new Map<string, Observable<DoodOpenCheck | null>>();
  private onSiteLocationsInFlightByShopId = new Map<
    string,
    Observable<DoodOnSiteLocationModel[]>
  >();

  constructor(
    private onSiteLocationDispatcher: OnSiteLocationStoreDispatcher,
    private onSiteLocationSelector: OnSiteLocationStoreSelector,
    private paymentAtTableDispatcher: PaymentAtTableStoreDispatcher,
    private paymentDispatcher: PaymentStoreDispatcher,
    private readonly onSiteLocationsApiService: OnSiteLocationsApiService,
  ) {}

  loadOnSiteLocations$(shopId: string): Observable<DoodOnSiteLocationModel[]> {
    const cachedLocations = this.getCachedOnSiteLocations(shopId);
    if (cachedLocations !== null) {
      return of(cachedLocations);
    }

    const existingInFlight = this.onSiteLocationsInFlightByShopId.get(shopId);
    if (existingInFlight) {
      return existingInFlight;
    }

    const request$ = defer(() =>
      this.onSiteLocationsApiService.getOnSiteLocations$(shopId).pipe(
        map(onSiteLocations => this.filterEnabledOnSiteLocations(onSiteLocations)),
        tap(onSiteLocations => this.onSiteLocationDispatcher.setLocations(onSiteLocations, shopId)),
      ),
    ).pipe(
      finalize(() => this.onSiteLocationsInFlightByShopId.delete(shopId)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.onSiteLocationsInFlightByShopId.set(shopId, request$);
    return request$;
  }

  private getCachedOnSiteLocations(shopId: string): DoodOnSiteLocationModel[] | null {
    if (
      !this.onSiteLocationSelector.locationsShopId ||
      this.onSiteLocationSelector.locationsShopId !== shopId
    ) {
      return null;
    }

    return this.filterEnabledOnSiteLocations(this.onSiteLocationSelector.locations);
  }

  private filterEnabledOnSiteLocations(
    onSiteLocations: DoodOnSiteLocationModel[],
  ): DoodOnSiteLocationModel[] {
    return onSiteLocations.filter(onSiteLocation => onSiteLocation[OnSiteLocationKeys.Enabled]);
  }

  loadOnSiteLocationOpenCheck$(onSiteLocationId: string): Observable<DoodOpenCheck | null> {
    const existingInFlight = this.openCheckInFlightByLocationId.get(onSiteLocationId);
    if (existingInFlight) {
      return existingInFlight;
    }

    const request$ = defer(() =>
      this.onSiteLocationsApiService.getOnSiteLocationOpenCheck$(onSiteLocationId).pipe(
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, index) => {
              // Retry seulement pour les erreurs 500 et jusqu'à 5 fois
              if (index >= 5 || (error.status && error.status !== 500)) {
                return throwError(() => error);
              }

              // Délai exponentiel : 100ms, 200ms, 400ms, 800ms, 1600ms
              const delayTime = Math.pow(2, index) * 100;
              return of(error).pipe(delay(delayTime));
            }),
          ),
        ),
      ),
    ).pipe(
      finalize(() => this.openCheckInFlightByLocationId.delete(onSiteLocationId)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.openCheckInFlightByLocationId.set(onSiteLocationId, request$);
    return request$;
  }

  getOnSiteLocationCheck$(checkId: string): Observable<DoodOpenCheck> {
    return this.onSiteLocationsApiService.getOnSiteLocationCheck$(checkId).pipe(
      catchError(error => {
        this.paymentAtTableDispatcher.updateCheck({
          error: error && error.detail ? { detail: error.detail } : null,
        });
        return throwError(() => error);
      }),
      tap((result: DoodOpenCheck) => {
        this.paymentAtTableDispatcher.updateCheck({ ...result, error: null });
      }),
    );
  }

  createCheckTransaction$(
    checkId: string,
    transaction: Partial<DoodOpenCheckTransaction>,
  ): Observable<DoodOpenCheckTransaction> {
    return this.onSiteLocationsApiService.createCheckTransaction$(checkId, transaction).pipe(
      tap(result => {
        this.paymentAtTableDispatcher.updateCheck({ active_transaction: result });
      }),
    );
  }

  getOnSiteLocationCheckTransactions$(transactionId: string): Observable<DoodOpenCheckTransaction> {
    return this.onSiteLocationsApiService.getOnSiteLocationCheckTransactions$(transactionId);
  }

  getOnSiteLocationCheckTransactionsPayment$(
    transactionId: string,
  ): Observable<IStripePaymentIntent | IPaygreenPaymentIntent> {
    return this.onSiteLocationsApiService.getOnSiteLocationCheckTransactionsPayment$(transactionId);
  }

  sendCheckTransactionInvoice$(
    transactionId: string,
    data: { email: string; fullName: string },
  ): Observable<IStripePaymentIntent> {
    return this.onSiteLocationsApiService.sendCheckTransactionInvoice$(transactionId, data);
  }

  getUserChecks$(userId: string): Observable<DoodOpenCheck[]> {
    return this.onSiteLocationsApiService.getUserChecks$(userId);
  }

  getUserCheck$(checkId: string): Observable<DoodOpenCheck> {
    return this.onSiteLocationsApiService.getUserCheck$(checkId);
  }

  /**
   * Injects a mock check into the store for manual/e2e testing.
   * Same behavior as getOnSiteLocationCheck$ but without API call.
   * Overrides check.id with PAT_MOCK_CHECK_ID so guards can recognize mock state.
   */
  injectMockCheck(check: DoodOpenCheck): void {
    const checkWithMockId = { ...check, id: PAT_MOCK_CHECK_ID };
    this.paymentAtTableDispatcher.updateCheck({ ...checkWithMockId, error: null });
    this.onSiteLocationDispatcher.updateSettings({
      id: check.onsite_location.id,
      lastLocationId: check.onsite_location.id,
    });
    this.paymentAtTableDispatcher.updateChoose({
      type: DoodPaymentAtTableAmountType.Total,
      amount: check.outstanding_amount,
    });

    const descriptors = (check.available_payment_methods ?? [])
      .filter(method => method.valid)
      .map(method => method.payment_method);

    if (descriptors.length > 0) {
      this.paymentDispatcher.updateMethods(descriptors as DoodPaymentMethodTypeModel[]);
    }
  }
}
